// @ts-check

import { html, render } from "lit-html";

const dialogClassName = "myDialogPrompt";
const dialogStyleId = "dialog-styles-injected";

/**
 * @returns {HTMLStyleElement}
 */
function injectDialogStyles() {
  const existingStyle = document.getElementById(dialogStyleId);
  if (existingStyle instanceof HTMLStyleElement) {
    return existingStyle;
  }

  const style = document.createElement("style");
  style.id = dialogStyleId;
  style.textContent = `
    .myDialogPrompt {
      border: none;
      border-radius: 0.5rem;
      padding: 1.25rem;
      min-width: 300px;
      max-width: 80%;
      box-sizing: border-box;
      font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
      background-color: #ffffff;
      box-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
      margin: auto;
    }

    .myDialogPrompt::backdrop {
      background-color: rgba(17, 24, 39, 0.7);
    }

    .myDialogPrompt form {
      margin: 0;
    }

    .myDialogPrompt .dialog-message {
      margin-bottom: 1.25rem;
      font-size: 0.875rem;
      color: #111827;
      white-space: pre-wrap;
      word-wrap: break-word;
      overflow-wrap: break-word;
    }

    .myDialogPrompt .dialog-actions {
      text-align: right;
      margin-top: 1.25rem;
    }

    .myDialogPrompt button {
      cursor: pointer;
      border-radius: 0.375rem;
      padding: 0.5rem 0.75rem;
      font-size: 0.875rem;
      font-weight: 600;
      border: none;
    }

    .myDialogPrompt button.primary {
      background-color: #4f46e5;
      color: #ffffff;
      box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
    }

    .myDialogPrompt button.primary:hover {
      background-color: #6366f1;
    }

    .myDialogPrompt button.secondary {
      background-color: #6b7280;
      color: #ffffff;
      margin-right: 0.5rem;
      box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
    }

    .myDialogPrompt button.secondary:hover {
      background-color: #9ca3af;
    }

    .myDialogPrompt input {
      width: 100%;
      box-sizing: border-box;
      padding: 0.375rem 0.75rem;
      font-size: 0.875rem;
      margin-bottom: 1.25rem;
      border-radius: 0.375rem;
      border: 0;
      color: #111827;
      box-shadow: 0 0 0 1px #d1d5db inset;
    }

    .myDialogPrompt input:focus {
      outline: none;
      box-shadow: 0 0 0 2px #4f46e5 inset;
    }
  `;

  document.head.appendChild(style);
  return style;
}

function removeDialogStyles() {
  const style = document.getElementById(dialogStyleId);
  if (style) {
    style.remove();
  }
}

/**
 * @returns {HTMLDialogElement}
 */
function createDialog() {
  injectDialogStyles();

  const dialog = document.createElement("dialog");
  dialog.className = dialogClassName;
  document.body.appendChild(dialog);
  return dialog;
}

/**
 * @param {HTMLDialogElement} dialog
 * @returns {void}
 */
function destroyDialog(dialog) {
  render(null, dialog);
  dialog.remove();
  removeDialogStyles();
}

/**
 * @param {{ message: string }} options
 * @returns {Promise<void>}
 */
export async function dAlert({ message }) {
  const dialog = createDialog();
  const view = html`
    <form method="dialog">
      <div class="dialog-message">${message}</div>
      <div class="dialog-actions">
        <button type="submit" value="ok" class="primary">OK</button>
      </div>
    </form>
  `;

  render(view, dialog);

  return new Promise((resolve) => {
    dialog.addEventListener("close", () => {
      resolve();
      destroyDialog(dialog);
    });

    dialog.showModal();
  });
}

/**
 * @param {{ message: string }} options
 * @returns {Promise<boolean>}
 */
export async function dConfirm({ message }) {
  const dialog = createDialog();
  const view = html`
    <form method="dialog">
      <div class="dialog-message">${message}</div>
      <div class="dialog-actions">
        <button
          type="button"
          value="cancel"
          class="secondary"
          @click=${() => dialog.close("cancel")}
        >
          Cancel
        </button>
        <button type="submit" value="ok" class="primary">OK</button>
      </div>
    </form>
  `;

  render(view, dialog);

  return new Promise((resolve) => {
    dialog.addEventListener("close", () => {
      const result = dialog.returnValue === "ok";
      resolve(result);
      destroyDialog(dialog);
    });

    dialog.showModal();
  });
}

/**
 * @param {{ message: string, type?: string }} options
 * @returns {Promise<string | null>}
 */
export async function dPrompt({ message, type = "text" }) {
  const dialog = createDialog();
  const autoComplete = type === "password" ? "new-password" : "off";
  const view = html`
    <form method="dialog">
      <div class="dialog-message">${message}</div>
      <input type=${type} name="promptInput" autocomplete=${autoComplete} />
      <div class="dialog-actions">
        <button
          type="button"
          value="cancel"
          class="secondary"
          @click=${() => dialog.close("cancel")}
        >
          Cancel
        </button>
        <button type="submit" value="ok" class="primary">OK</button>
      </div>
    </form>
  `;

  render(view, dialog);

  return new Promise((resolve) => {
    dialog.addEventListener("close", () => {
      const input = dialog.querySelector("input[name='promptInput']");
      let result = null;

      if (dialog.returnValue === "ok" && input instanceof HTMLInputElement) {
        result = input.value;
      }

      resolve(result);
      destroyDialog(dialog);
    });

    dialog.showModal();

    const input = dialog.querySelector("input[name='promptInput']");
    if (input instanceof HTMLInputElement) {
      input.focus();
    }
  });
}
